import { useCallback, useEffect, useState } from 'react';
import { MdCloudOff } from 'react-icons/md';
import Layout from '../../components/Layout';
import { listarFalhadas, requeueFalhadas, requeuePermissoes } from '../../sync/engine';
import { useSync } from '../../contexts/SyncContext';
import { SYNC_SCHEMA } from '../../sync/schema';

const OP_LABEL = { create: 'Criação', update: 'Edição', delete: 'Exclusão' };

const TIPO_CHIP = {
  permissao: 'bg-red-100 text-red-800',
  validacao: 'bg-yellow-100 text-yellow-800',
  rede: 'bg-gray-100 text-gray-700',
};
const TIPO_LABEL = {
  permissao: 'Permissão negada',
  validacao: 'Erro de validação',
  rede: 'Erro de rede',
};

function modeloLabel(chave) {
  const schema = SYNC_SCHEMA[chave];
  if (!schema) return chave;
  return schema.modelo.split('.').pop();
}

export default function FilaOffline() {
  const { sincronizar } = useSync();
  const [carregando, setCarregando] = useState(true);
  const [entradas, setEntradas] = useState([]);
  const [processando, setProcessando] = useState(new Set());

  const carregar = useCallback(async () => {
    setCarregando(true);
    setEntradas(await listarFalhadas());
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const formatarData = (iso) =>
    iso ? new Date(iso).toLocaleString('pt-BR') : '—';

  const reenviarEntrada = async (queueId) => {
    setProcessando((prev) => { const s = new Set(prev); s.add(queueId); return s; });
    await requeueFalhadas([queueId]);
    sincronizar();
    await carregar();
    setProcessando((prev) => { const s = new Set(prev); s.delete(queueId); return s; });
  };

  const reenviarTodasPermissoes = async () => {
    await requeuePermissoes();
    sincronizar();
    await carregar();
  };

  const qtdPermissao = entradas.filter((e) => e.tipo_falha === 'permissao').length;

  return (
    <Layout>
      <div className="p-6 font-medium max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            Fila Offline com Falha <MdCloudOff size={34} />
          </h2>
          <button
            onClick={carregar}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Atualizar
          </button>
        </div>

        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Lançamentos gravados em campo que não chegaram ao servidor. Erros de{' '}
          <strong>permissão</strong> aconteceram por causa do bug de sincronização (já corrigido no
          servidor) — basta reenviar. Erros de <strong>validação</strong> indicam dado inválido e
          precisam de verificação. Erros de <strong>rede</strong> são reenviados automaticamente
          quando a conexão for restabelecida.
        </div>

        {qtdPermissao > 0 && (
          <div className="mb-4 flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <span className="text-sm text-green-800 flex-1">
              {qtdPermissao} entrada(s) travada(s) por permissão — o servidor já aceita esses dados.
            </span>
            <button
              onClick={reenviarTodasPermissoes}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Reenviar todos
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <p className="text-gray-600">{entradas.length} entrada(s) com falha</p>
          </div>

          {carregando ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Modelo</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Operação</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Quando</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Motivo</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {entradas.map((e) => (
                    <tr
                      key={e.queue_id}
                      className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-3 text-sm font-mono">{modeloLabel(e.tabela)}</td>
                      <td className="p-3 text-sm">{OP_LABEL[e.op] ?? e.op}</td>
                      <td className="p-3 text-sm">{formatarData(e.criado_em)}</td>
                      <td className="p-3 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${TIPO_CHIP[e.tipo_falha] ?? 'bg-gray-100 text-gray-600'}`}
                        >
                          {TIPO_LABEL[e.tipo_falha] ?? 'Desconhecido'}
                        </span>
                      </td>
                      <td
                        className="p-3 text-sm text-gray-500 max-w-xs truncate"
                        title={e.detalhe ?? ''}
                      >
                        {e.detalhe ?? '—'}
                      </td>
                      <td className="p-3 text-sm">
                        <button
                          onClick={() => reenviarEntrada(e.queue_id)}
                          disabled={processando.has(e.queue_id)}
                          className="text-blue-600 hover:text-blue-800 underline disabled:opacity-40"
                        >
                          {processando.has(e.queue_id) ? 'Reenviando...' : 'Reenviar'}
                        </button>
                      </td>
                    </tr>
                  ))}

                  {entradas.length === 0 && (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-gray-500">
                        Nenhuma entrada com falha. Tudo certo!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
